if (!customElements.get('reviews-carousel')) {
  class ReviewsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.reviews-carousel__track');
      this.slides = Array.from(this.querySelectorAll('.reviews-carousel__slide'));
      if (!this.track || this.slides.length === 0) return;

      this.viewport = this.querySelector('.reviews-carousel__viewport');
      this.dotsContainer = this.querySelector('.reviews-carousel__dots');
      this.prevBtn = this.querySelector('.reviews-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.reviews-carousel__arrow--next');

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
      this.infinite = this.dataset.infinite === 'true';
      this.mobileLayoutSetting = parseFloat(this.dataset.mobileLayout) || 1;

      this.currentIndex = 0;
      this.slidesPerView = 1;
      this.slideWidth = 0;
      this.maxIndex = 0;
      this.translateX = 0;
      this.autoplayTimer = null;
      this.isDragging = false;
      this.wasDragged = false;
      this.startX = 0;
      this.startTranslateX = 0;
      this.dragDelta = 0;
      this.lastWidth = 0;

      this._onPrevClick = this._onPrevClick.bind(this);
      this._onNextClick = this._onNextClick.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onDragMove = this._onDragMove.bind(this);
      this._onDragEnd = this._onDragEnd.bind(this);
      this._onTrackClick = this._onTrackClick.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);

      this._init();
    }

    disconnectedCallback() {
      this._destroy();
    }

    _init() {
      this._bindEvents();
      this._setupResizeObserver();
      this._setupIntersectionObserver();

      if (this.viewport && this.viewport.clientWidth > 0) {
        this._measure();
      }
    }

    _destroy() {
      this._stopAutoplay();

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);

      if (this.track) {
        this.track.removeEventListener('mousedown', this._onDragStart);
        this.track.removeEventListener('touchstart', this._onDragStart);
        this.track.removeEventListener('click', this._onTrackClick, true);
      }

      document.removeEventListener('mousemove', this._onDragMove);
      document.removeEventListener('mouseup', this._onDragEnd);
      document.removeEventListener('touchmove', this._onDragMove);
      document.removeEventListener('touchend', this._onDragEnd);

      this.removeEventListener('mouseenter', this._onMouseEnter);
      this.removeEventListener('mouseleave', this._onMouseLeave);
    }

    _bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);

      this.track.addEventListener('mousedown', this._onDragStart);
      this.track.addEventListener('touchstart', this._onDragStart, { passive: true });
      this.track.addEventListener('click', this._onTrackClick, true);

      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
    }

    _setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = entry.contentRect.width;
          if (w > 0 && w !== this.lastWidth) {
            this._measure();
          }
        }
      });
      this.resizeObserver.observe(this.viewport);
    }

    _setupIntersectionObserver() {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (this.autoplayEnabled) this._startAutoplay();
            } else {
              this._stopAutoplay();
            }
          });
        },
        { threshold: 0.15 }
      );
      this.intersectionObserver.observe(this);
    }

    /* ---- Measurement ---- */

    _measure() {
      const containerWidth = this.viewport.clientWidth;
      if (containerWidth === 0) return;
      this.lastWidth = containerWidth;

      const screenWidth = window.innerWidth;

      if (screenWidth < 750) {
        this.slidesPerView = this.mobileLayoutSetting;
      } else if (screenWidth < 990) {
        this.slidesPerView = 2;
      } else {
        this.slidesPerView = 3;
      }

      this.slideWidth = containerWidth / this.slidesPerView;
      this.maxIndex = Math.max(0, this.slides.length - Math.floor(this.slidesPerView));

      this.slides.forEach((slide) => {
        slide.style.width = this.slideWidth + 'px';
      });

      if (this.currentIndex > this.maxIndex) {
        this.currentIndex = this.maxIndex;
      }

      this._buildDots();
      this._goTo(this.currentIndex, false);

      if (this.autoplayEnabled) this._startAutoplay();
    }

    /* ---- Navigation ---- */

    _goTo(index, animate) {
      if (animate === undefined) animate = true;

      this.currentIndex = index;
      this.translateX = -(this.currentIndex * this.slideWidth);

      if (animate) {
        this.track.classList.add('is-animating');
      } else {
        this.track.classList.remove('is-animating');
      }

      this.track.style.transform = 'translate3d(' + this.translateX + 'px, 0, 0)';
      this._updateDots();
    }

    _prev() {
      if (this.currentIndex > 0) {
        this._goTo(this.currentIndex - 1);
      } else if (this.infinite && this.maxIndex > 0) {
        this._goTo(this.maxIndex);
      }
    }

    _next() {
      if (this.currentIndex < this.maxIndex) {
        this._goTo(this.currentIndex + 1);
      } else if (this.infinite && this.maxIndex > 0) {
        this._goTo(0);
      }
    }

    /* ---- Dots ---- */

    _buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      const count = this.maxIndex + 1;
      if (count <= 1) return;

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.className = 'reviews-carousel__dot';
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        if (i === this.currentIndex) dot.classList.add('is-active');

        dot.addEventListener('click', () => {
          this._pauseAndResume();
          this._goTo(i);
        });

        this.dotsContainer.appendChild(dot);
      }
    }

    _updateDots() {
      if (!this.dotsContainer) return;
      const dots = this.dotsContainer.querySelectorAll('.reviews-carousel__dot');
      dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === this.currentIndex);
      });
    }

    /* ---- Drag / Swipe ---- */

    _getPointerX(e) {
      return e.type.indexOf('touch') >= 0 ? e.touches[0].clientX : e.clientX;
    }

    _onDragStart(e) {
      if (e.button && e.button !== 0) return;
      this.isDragging = true;
      this.wasDragged = false;
      this.dragDelta = 0;
      this.startX = this._getPointerX(e);
      this.startTranslateX = this.translateX;

      this.track.classList.remove('is-animating');

      this._stopAutoplay();

      document.addEventListener('mousemove', this._onDragMove);
      document.addEventListener('mouseup', this._onDragEnd);
      document.addEventListener('touchmove', this._onDragMove, { passive: false });
      document.addEventListener('touchend', this._onDragEnd);
    }

    _onDragMove(e) {
      if (!this.isDragging) return;

      const currentX = this._getPointerX(e);
      this.dragDelta = currentX - this.startX;

      if (!this.wasDragged && Math.abs(this.dragDelta) > 5) {
        this.wasDragged = true;
        this.track.classList.add('is-dragging');
      }

      if (!this.wasDragged) return;

      var newTranslate = this.startTranslateX + this.dragDelta;

      var minTranslate = -(this.maxIndex * this.slideWidth);
      var maxTranslate = 0;

      if (newTranslate > maxTranslate) {
        newTranslate = maxTranslate + (newTranslate - maxTranslate) * 0.3;
      } else if (newTranslate < minTranslate) {
        newTranslate = minTranslate + (newTranslate - minTranslate) * 0.3;
      }

      this.track.style.transform = 'translate3d(' + newTranslate + 'px, 0, 0)';

      if (e.cancelable) {
        e.preventDefault();
      }
    }

    _onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      this.track.classList.remove('is-dragging');

      document.removeEventListener('mousemove', this._onDragMove);
      document.removeEventListener('mouseup', this._onDragEnd);
      document.removeEventListener('touchmove', this._onDragMove);
      document.removeEventListener('touchend', this._onDragEnd);

      if (this.wasDragged) {
        var threshold = this.slideWidth * 0.2;

        if (this.dragDelta < -threshold) {
          this._next();
        } else if (this.dragDelta > threshold) {
          this._prev();
        } else {
          this._goTo(this.currentIndex);
        }
      }

      if (this.autoplayEnabled) {
        this._startAutoplay();
      }
    }

    _onTrackClick(e) {
      if (this.wasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    /* ---- Autoplay ---- */

    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this._next();
      }, this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _pauseAndResume() {
      this._stopAutoplay();
      if (this.autoplayEnabled) {
        this._startAutoplay();
      }
    }

    /* ---- Hover pause ---- */

    _onMouseEnter() {
      this._stopAutoplay();
    }

    _onMouseLeave() {
      if (this.autoplayEnabled) {
        this._startAutoplay();
      }
    }

    /* ---- Arrow handlers ---- */

    _onPrevClick(e) {
      e.preventDefault();
      this._pauseAndResume();
      this._prev();
    }

    _onNextClick(e) {
      e.preventDefault();
      this._pauseAndResume();
      this._next();
    }
  }

  customElements.define('reviews-carousel', ReviewsCarousel);
}
